import React, {useContext, useEffect, useState} from 'react';
import {Box, Button, Grid, TextField, Typography} from '@mui/material';
import {IMaskMixin} from 'react-imask';
import {setNestedObjectValues} from 'formik';
import {useSnackbar} from 'notistack';
import withAutoForm, {
  AutoFormProps,
  FormReadOnly,
  FormReadOnlyContext,
} from '../../components/AutoForm/AutoFormNew';
import {AutoField, SelectOption} from '../../components/AutoForm/AutoFieldNew';
import {useAutoField, useAutoForm} from '../../components/AutoForm/useAutoForm';
import usePlanNamesProviders from '../../hooks/usePlanNamesProviders';
import {useAccountContext} from '../../contexts/AccountContext';
import {Panel} from '../../components/Panel/Panel';
import {ScrollableModal} from '../../components/Common/ScrollableModal';
import UnfinishedFormPrompt from '../../components/Common/UnfinishedFormPrompt';
import {sortAlphabeticallyByName} from '../bulkAdmin/useBulkAdmin';
import {getContactName} from '../../utils/getContactName';
import validateUUID from '../../utils/validateUUID';
import OWNERSHIP from '../../constants/Ownership';
import {BankDetailsEnum} from '../../schema-types/globalTypes';
import {AccountBasicFragment_contacts} from '../../schema-types/AccountBasicFragment';
import {SaltusAny} from '../../utils/SaltusAny';
import {UPDATES} from '../../shared/UPDATES';
import useSaveBankAccount from './useSaveBankAccount';
import {
  bankAccountNameField,
  bankAccountNumberField,
  bankAccountValues,
  bankContactField,
  bankDetailsField,
  bankProviderField,
} from './bankAccountFields';

const BankAccountContactField: React.FC = () => {
  const [, , contactHelpers] = useAutoField<string | undefined>('contactId');
  const {account} = useAccountContext();
  const contacts = account?.contacts || [];

  useEffect(() => {
    // set contact if only one
    if (contacts.length === 1) {
      contactHelpers.setValue(UPDATES._D5100, contacts[0].id);
    }
  }, [contacts]);

  const contactOptions: SelectOption[] = contacts.map((contact) => ({
    label: getContactName(contact),
    value: contact.id,
  }));
  if (contactOptions.length !== 1) {
    contactOptions.push({
      label: 'Joint',
      value: OWNERSHIP.JOINT_CODE,
    });
  }

  return <AutoField field={{...bankContactField, options: contactOptions}} />;
};

const SortCodeInternalMaskTextField = IMaskMixin((props) => (
  <TextField
    fullWidth
    label="Sort code"
    variant="outlined"
    className="sentry-mask"
    size="small"
    InputLabelProps={{shrink: true}}
    {...(props as any)}
  />
));

const SortCodeField = React.forwardRef<HTMLElement>(function SortCodeField(props, ref) {
  const [sortCodeField, meta, {setValue: setSortCode}] = useAutoField<string | undefined>(
    'sortCode'
  );
  const {readOnly: readOnlyContext} = useContext(FormReadOnlyContext);
  return (
    <SortCodeInternalMaskTextField
      // @ts-ignore
      error={meta.error}
      value={sortCodeField.value || ''}
      mask="00-00-00"
      definitions={{
        '#': /[1-9]/,
      }}
      disabled={readOnlyContext}
      inputRef={ref as any}
      onAccept={(value: SaltusAny) => setSortCode(UPDATES._D5101, value)}
      overwrite
    />
  );
});

const NewBankAccountFormInner: React.FC = () => {
  const {accountId} = useAccountContext();
  const [showNewProviderModal, setShowNewProviderModal] = useState(false);
  const {values, dirty, validateForm, setTouched} = useAutoForm<bankAccountValues>();
  const {enqueueSnackbar, closeSnackbar} = useSnackbar();
  const {save, saving, submitting} = useSaveBankAccount();
  const {rawProviders, queryGetProductProviders} = usePlanNamesProviders();

  useEffect(() => {
    if (!rawProviders && accountId) {
      queryGetProductProviders({
        variables: {
          accountId: validateUUID(accountId),
        },
      });
    }
  }, [rawProviders, accountId]);

  const isNotKnown = values.bankDetails === BankDetailsEnum.NOT_KNOWN;

  const submitForm = async () => {
    closeSnackbar();
    const validationErrors = await validateForm();
    setTouched(setNestedObjectValues({...validationErrors}, true) as SaltusAny);
    if (Object.keys(validationErrors).length > 0) {
      enqueueSnackbar('Form not valid. Please check fields', {variant: 'error'});
      return;
    }
    await save(values);
  };

  return (
    <>
      {/* No onClose: the modal is not dismissible while submission is in flight. */}
      <ScrollableModal size="small" open={submitting}>
        <Box flex={1} display="flex" alignItems="center" justifyContent="center">
          <Typography fontSize={16}>Bank account is being submitted...</Typography>
        </Box>
      </ScrollableModal>
      <ScrollableModal
        size="small"
        open={showNewProviderModal}
        onClose={() => setShowNewProviderModal(false)}
      >
        <Panel title="Request new provider">
          <Typography>
            Please contact our support desk if you require an additional provider to be
            added:{' '}
          </Typography>
          <Button
            style={{textTransform: 'lowercase'}}
            href="mailto:data@saltus.co.uk"
            type="button"
            variant="text"
          >
            data@saltus.co.uk
          </Button>
        </Panel>
      </ScrollableModal>
      <Grid container direction="row" spacing={2}>
        <Grid item xs={6}>
          <AutoField field={bankDetailsField} />
        </Grid>
        <Grid item xs={6}>
          <AutoField
            field={{
              ...bankProviderField,
              options:
                rawProviders?.productProviders
                  ?.filter((x) => x.isBank)
                  .sort(sortAlphabeticallyByName)
                  .map<SelectOption>((x) => ({label: x.name!, value: x.id})) || [],
            }}
          />
          <Button
            onClick={() => setShowNewProviderModal(true)}
            type="button"
            variant="text"
            size="small"
            style={{marginBottom: 0}}
          >
            Request new provider
          </Button>
        </Grid>
        <Grid item xs={6}>
          <AutoField field={bankAccountNameField} />
        </Grid>
        <Grid item xs={6}>
          <BankAccountContactField />
        </Grid>

        <FormReadOnly readOnly={isNotKnown}>
          <Grid item xs={6}>
            <AutoField disabled={isNotKnown} field={bankAccountNumberField} />
          </Grid>

          <Grid className="sentry-mask" item xs={6}>
            <SortCodeField />
          </Grid>
        </FormReadOnly>
      </Grid>
      <Box mt={3} display="flex" justifyContent="flex-end">
        <UnfinishedFormPrompt path={`/account/${accountId}`} dirty={dirty} />
        <Box ml={1}>
          <Button
            variant="contained"
            color="primary"
            disabled={saving || submitting}
            onClick={submitForm}
          >
            Save
          </Button>
        </Box>
      </Box>
    </>
  );
};

export const NewBankAccountForm = withAutoForm<
  bankAccountValues,
  AutoFormProps<bankAccountValues>
>(NewBankAccountFormInner);
